import { useMemo } from "react";
import { useStudentById } from "hooks/api/students";
import { Button } from "components/Interactive/Button";
import { Space } from "components/Common/Space";
import { Avatar } from "components/Common/Avatar";
import { Tooltip } from "components/Interactive/Tooltip";
import "./StudentSwitcher.styles.scss";

type StudentSwitcherProps = {
  /**
   * Array of student IDs from group members
   */
  studentIds: number[];
  /**
   * Currently selected student ID
   */
  selectedStudentId?: number;
  /**
   * Callback when student is selected
   */
  onStudentSelect: (studentId: number) => void;
  /**
   * Loading state
   */
  loading?: boolean;
};

type StudentButtonProps = {
  studentId: number;
  isSelected: boolean;
  onSelect: (studentId: number) => void;
};

const StudentButton: React.FC<StudentButtonProps> = ({
  studentId,
  isSelected,
  onSelect,
}) => {
  const { data: student, isLoading } = useStudentById(studentId.toString());

  if (isLoading || !student?.person) {
    return (
      <Button
        variant={isSelected ? "primary" : "secondary"}
        disabled
        className="nb-student-switcher-button"
      >
        Loading...
      </Button>
    );
  }

  const fullName = `${student.person.firstName} ${student.person.lastName}`;
  const initials = `${student.person.firstName.charAt(
    0
  )}${student.person.lastName.charAt(0)}`;

  return (
    <Tooltip
      text={fullName}
      position="top"
      trigger="hover"
      spaceSize="small"
      variant="compact"
    >
      <Button
        variant={isSelected ? "primary" : "secondary"}
        onClick={() => onSelect(studentId)}
        className="nb-student-switcher-button"
      >
        <Space direction="horizontal" size="x-small" align="center">
          <Avatar
            size="small"
            image={student.person.photoUrl}
            emoji={initials}
            color={isSelected ? "blue" : "grey"}
          />
          <span className="nb-student-switcher-name">{fullName}</span>
        </Space>
      </Button>
    </Tooltip>
  );
};

export const StudentSwitcher: React.FC<StudentSwitcherProps> = ({
  studentIds,
  selectedStudentId,
  onStudentSelect,
  loading = false,
}) => {
  const validStudentIds = useMemo(
    () => studentIds.filter((id) => id != null && !isNaN(id)),
    [studentIds]
  );

  if (loading || validStudentIds.length === 0) {
    return null;
  }

  return (
    <div className="nb-student-switcher">
      <Space direction="horizontal" size="x-small" wrap={false}>
        {validStudentIds.map((studentId) => (
          <StudentButton
            key={studentId}
            studentId={studentId}
            isSelected={selectedStudentId === studentId}
            onSelect={onStudentSelect}
          />
        ))}
      </Space>
    </div>
  );
};
